'use client';

import { Button } from 'ui';
import { StaticIcon } from 'ui/components/Icon/Icon';
import { ShoppingCart } from 'ui/icons/icons/Growth/ShoppingCart';
import { Drawer } from 'ui/components/Drawer/drawer';
import { getFormatters } from 'ui/hooks/formatter';
import { getCurrencyFromRegion, getRegionFromRequest } from '@whoop/i18n';
import { useTranslation } from '@whoop/i18n/lang/client';
import CartItem from './CartItem';
import BillingDetails from './BillingDetails';
import useLocale from '@/hooks/useLocale';
import { useCartStore } from '@/stores/StoreProvider';
import { useNavigation } from '@/hooks/useNavigation';

interface ICart {
  cartLabel: string;
}

function Cart({ cartLabel }: ICart) {
  const { cart, isCartOpen, setIsCartOpen } = useCartStore((state) => ({
    cart: state.cart,
    isCartOpen: state.isCartOpen,
    setIsCartOpen: state.setIsCartOpen,
  }));

  const { goTo } = useNavigation();
  const cartId = cart?.id ?? null;
  const cartItems = cart?.cartItems ?? [];
  const { language, country } = useLocale();
  const { t } = useTranslation('cart');
  const region = getRegionFromRequest(country.toLocaleUpperCase());
  const currency = getCurrencyFromRegion(region);
  const { formatDisplayPrice } = getFormatters({
    currency,
    language,
    region,
  });

  const cartItemCount = cart?.cartItems?.reduce(
    (acc, item) => acc + item.quantity,
    0,
  );

  return (
    <>
      <Button
        aria-label='cart'
        className='flex w-auto gap-2 whitespace-nowrap px-3 text-white hover:bg-black xl:px-5'
        onClick={() => setIsCartOpen(!isCartOpen)}
        size='medium'
        variant='transparent'
      >
        <StaticIcon
          IconComponent={ShoppingCart}
          alt='cart'
          color='#ffffff'
          size='small'
        />
        <span className='hidden xl:inline'>{cartLabel}</span>
        <span className='bg-black text-white'>({cartItemCount || '--'})</span>
      </Button>

      <Drawer
        className='bg-gray-50 sm:w-[400px]'
        isOpen={isCartOpen}
        setIsOpen={setIsCartOpen}
      >
        <h2 className='mb-4 text-xl font-bold'>{t('yourCart')}</h2>
        {cartId && cartItems.length > 0 ? (
          <>
            <section className='mb-6 rounded-lg bg-white p-4'>
              {cart?.cartItems.map((item) => (
                <CartItem
                  cartId={cartId}
                  formatDisplayPrice={formatDisplayPrice}
                  item={item}
                  key={item.key}
                />
              ))}
            </section>

            <section className='mb-6 rounded-lg bg-white p-4'>
              {cart?.billingDetails ? (
                <BillingDetails
                  billingDetails={cart?.billingDetails ?? []}
                  formatDisplayPrice={formatDisplayPrice}
                />
              ) : null}
              <Button
                className='mb-4'
                onClick={() => setIsCartOpen(false)}
                variant='primary'
              >
                {t('keepShopping')}
              </Button>
              <Button
                disabled={Boolean(!cartId)}
                onClick={() => goTo('checkout')}
                variant='secondary'
              >
                {t('checkout')}
              </Button>
            </section>
          </>
        ) : (
          <p className='text-center text-gray-500'>{t('yourCartIsEmpty')}</p>
        )}
      </Drawer>
    </>
  );
}

export default Cart;
